import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import { validateCdcEnvelope } from 'new/utils/cdcEnvelopeValidator';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

interface CdcEnvelopeWarning {
  missingKeys: string[];
  isDismissed: boolean;
  onDismiss: () => void;
}

export function useCdcEnvelopeWarning(flowType: string | undefined): CdcEnvelopeWarning {
  const rawSchema = useSelector((state: any) => state.schemaEditor.toSchema.raw);
  const flatSamples = useSelector((state: any) => state.schemaEditor.toSamples?.toFlat);
  const inputByAttrId = useSelector((state: any) => state.schemaEditor.fromSchema.byAttrId);
  // toUpdatedAt is null until the user runs the transform at least once. Gate validation
  // on this to avoid false positives from stale schema carried over from a prior session
  // (SCHEMA_EDITOR_INIT preserves toSchema.raw from the previous Redux state).
  const samplesRanAt = useSelector((state: any) => state.schemaEditor.toSamples?.toUpdatedAt);
  const useV2 = useFeatureFlag('UI_CDC_ENVELOPE_V2');

  // Dot-notation field names from actual output samples — used as fallback when the schema
  // doesn't enumerate nested object properties (see validateCdcEnvelope).
  const flatFieldNames = useMemo(() => {
    if (!flatSamples?.length) return null;
    const names = new Set<string>();
    for (const sample of flatSamples) {
      if (sample?.attrs) Object.keys(sample.attrs).forEach((k) => names.add(k));
    }
    return names;
  }, [flatSamples]);

  const missingKeysStr = useMemo(
    () =>
      samplesRanAt
        ? validateCdcEnvelope(rawSchema, flowType ?? '', useV2, flatFieldNames, inputByAttrId).join(
            ',',
          )
        : '',
    [rawSchema, flowType, useV2, flatFieldNames, samplesRanAt, inputByAttrId],
  );

  const [isDismissed, setIsDismissed] = useState(false);
  useEffect(() => {
    setIsDismissed(false);
  }, [missingKeysStr]);

  return {
    missingKeys: missingKeysStr ? missingKeysStr.split(',') : [],
    isDismissed,
    onDismiss: () => setIsDismissed(true),
  };
}
